<script setup>
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the contact panel's "Flow
// responses" list for this conversation, read from the loaded messages
// (content_attributes.whatsapp_flow_response + kaption_flow).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { buildLabeledEntries } from 'dashboard/helper/kaptionWhatsappFlows';

const props = defineProps({
  messages: { type: Array, default: () => [] },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();

const booleanLabels = computed(() => ({
  yes: t('KAPTION_WHATSAPP_FLOWS.VALUES.YES'),
  no: t('KAPTION_WHATSAPP_FLOWS.VALUES.NO'),
}));

const responses = computed(() =>
  props.messages
    .filter(m => m?.content_attributes?.whatsapp_flow_response)
    .map(m => {
      const kaptionFlow = m.content_attributes.kaption_flow || null;
      return {
        id: m.id,
        createdAt: m.created_at,
        flowName: kaptionFlow?.flow_name || '',
        verified: !!kaptionFlow?.verified,
        entries: buildLabeledEntries(
          m.content_attributes.whatsapp_flow_response.response_json,
          kaptionFlow && {
            labels: kaptionFlow.labels,
            fields: kaptionFlow.fields,
            sensitiveKeys: kaptionFlow.sensitive_keys,
          },
          { showSensitive: isAdmin.value, booleanLabels: booleanLabels.value }
        ),
      };
    })
    .reverse()
);
</script>

<template>
  <div class="flex flex-col gap-3 px-4 pb-4" data-testid="kaption-flow-panel">
    <p v-if="!responses.length" class="text-sm text-n-slate-11">
      {{ t('KAPTION_WHATSAPP_FLOWS.CONTACT_PANEL.EMPTY') }}
    </p>
    <div
      v-for="response in responses"
      :key="response.id"
      class="p-3 border rounded-lg border-n-weak"
      data-testid="kaption-flow-panel-item"
    >
      <div class="flex items-center justify-between gap-2 mb-2">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{
            response.flowName || t('KAPTION_WHATSAPP_FLOWS.BUBBLE.COMPLETED')
          }}
        </span>
        <span class="text-xs text-n-slate-10 shrink-0">
          {{ messageTimestamp(response.createdAt, 'LLL d') }}
        </span>
      </div>
      <dl class="space-y-1.5">
        <div v-for="entry in response.entries" :key="entry.key">
          <dt class="text-xs text-n-slate-11">
            {{ entry.label }}
            <span
              v-if="entry.sensitive"
              class="i-lucide-lock size-3 align-[-1px]"
              data-testid="kaption-flow-panel-sensitive"
            />
          </dt>
          <dd class="text-sm break-words text-n-slate-12">{{ entry.value }}</dd>
        </div>
      </dl>
    </div>
  </div>
</template>
